import React from 'react';
import { Trophy, Star, Award, Zap, Lock, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Modelo estático (gamificação volta em fase futura — UI/conteúdo do original,
// dados locais como no modo demonstração)
const LEVELS = [
  { number: 1, title: 'Iniciante Astral', requiredPoints: 0 },
  { number: 2, title: 'Explorador', requiredPoints: 100 },
  { number: 3, title: 'Viajante Estelar', requiredPoints: 300 },
  { number: 4, title: 'Guardião Cósmico', requiredPoints: 600 },
];

const ACHIEVEMENTS = [
  { id: 'first-chat', icon: '💬', title: 'Primeira Conversa', description: 'Converse com o chat astral', points: 10, unlocked: false },
  { id: 'first-chart', icon: '🔮', title: 'Carta Revelada', description: 'Gere sua primeira carta astral', points: 20, unlocked: false },
  { id: 'horoscope-fan', icon: '🌙', title: 'Fã do Signo', description: 'Confira o horóscopo 7 dias seguidos', points: 30, unlocked: false },
  { id: 'consultant', icon: '🌟', title: 'Encontro Celestial', description: 'Fale com um consultor', points: 50, unlocked: false },
];

export const RewardsPage: React.FC = () => {
  const { user } = useAuth();
  const points = 0;

  if (!user) {
    return (
      <div className="relative min-h-[calc(100vh-4rem)] p-6">
        <div className="absolute inset-0 backdrop-blur-md bg-indigo-950/50 flex items-center justify-center z-10">
          <div className="max-w-md text-center p-8 bg-white/10 rounded-lg backdrop-blur-lg">
            <Trophy className="w-16 h-16 text-purple-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-purple-200 mb-4">
              Desbloqueie Seu Perfil Personalizado
            </h2>
            <p className="text-purple-200/70 mb-6">
              Crie sua conta agora e tenha acesso a:
            </p>
            <ul className="space-y-3 text-left mb-6">
              <li className="flex items-center text-purple-200/80">
                <Star className="w-5 h-5 mr-2 text-purple-300" />
                Sistema de níveis e recompensas
              </li>
              <li className="flex items-center text-purple-200/80">
                <Award className="w-5 h-5 mr-2 text-purple-300" />
                Conquistas exclusivas
              </li>
              <li className="flex items-center text-purple-200/80">
                <Zap className="w-5 h-5 mr-2 text-purple-300" />
                Benefícios especiais
              </li>
            </ul>
            <button
              onClick={() => window.location.href = '/signup'}
              className="w-full btn-primary"
            >
              Cadastre-se agora e aproveite os benefícios
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentLevel = LEVELS[0];

  return (
    <div className="p-6 pt-8 min-h-[calc(100vh-4rem)] space-y-6">
      <div className="flex items-center space-x-4 mb-8">
        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
          <Trophy className="w-8 h-8 text-purple-300" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-purple-200">Recompensas</h2>
          <p className="text-purple-300/70">Desbloqueie benefícios especiais</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Star className="w-5 h-5 text-purple-300" />
            <h4 className="font-medium text-purple-200">Pontos</h4>
          </div>
          <p className="text-2xl font-bold text-purple-200">{points}</p>
          <p className="text-sm text-purple-200/70 mt-1">
            Acumule pontos usando o app
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Award className="w-5 h-5 text-purple-300" />
            <h4 className="font-medium text-purple-200">Conquistas</h4>
          </div>
          <p className="text-2xl font-bold text-purple-200">
            0/{ACHIEVEMENTS.length}
          </p>
          <p className="text-sm text-purple-200/70 mt-1">
            Conquistas desbloqueadas
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Zap className="w-5 h-5 text-purple-300" />
            <h4 className="font-medium text-purple-200">Nível</h4>
          </div>
          <p className="text-2xl font-bold text-purple-200">{currentLevel.number}</p>
          <p className="text-sm text-purple-200/70 mt-1">
            {currentLevel.title}
          </p>
        </div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-purple-200">Progresso para o próximo nível</p>
          <span className="text-sm text-purple-200/70">0/100</span>
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-600" style={{ width: '0%' }} />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-bold text-purple-200 flex items-center">
          <Award className="w-6 h-6 mr-2" />
          Suas Conquistas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ACHIEVEMENTS.map((achievement) => (
            <div
              key={achievement.id}
              className={`bg-white/10 backdrop-blur-lg rounded-lg p-4 ${
                achievement.unlocked ? 'border border-purple-500/30' : 'opacity-70'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{achievement.icon}</span>
                  <h3 className="text-lg font-medium text-purple-200">
                    {achievement.title}
                  </h3>
                </div>
                {achievement.unlocked
                  ? <Check className="w-5 h-5 text-green-400" />
                  : <Lock className="w-5 h-5 text-purple-300/50" />}
              </div>
              <p className="text-sm text-purple-200/70 mb-2">
                {achievement.description}
              </p>
              <div className="text-sm text-purple-300">
                +{achievement.points} pontos
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};